// src/services/analyticsService.js
// FIX: We must import the `authenticatedFetch` function directly using curly braces {}
// because it is a named export from './api.js', not a default export.
import { authenticatedFetch } from './api';

// --- 🚀 ENTERPRISE UPDATE: Global WebSocket Integration ---
import webSocketService from './websocketService';
// ----------------------------------------------------------
// --- UPDATE (ML Ops / Analytics Hub) ---
import mlOpsService from './mlOpsService';
// --- END UPDATE ---

/**
 * Helper function to create a query string from a params object,
 * ignoring any null or empty values.
 * @param {object} params - The parameters to serialize.
 * @returns {string} - The URL query string.
 */
const createQueryString = (params) => {
  if (!params) return '';

  const cleanParams = {};
  for (const key in params) {
    if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
      cleanParams[key] = params[key];
    }
  }

  const queryString = new URLSearchParams(cleanParams).toString();
  return queryString ? `?${queryString}` : '';
};

// Helper to get default date range (Last 30 Days) if none provided
const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 30);
  return { startDate, endDate };
};

export const getKpis = (params) => {
  const queryString = createQueryString(params);
  return authenticatedFetch(`/analytics/kpis${queryString}`, { method: 'GET' });
};

export const getWidgetData = (widgetName, params) => {
  const queryString = createQueryString(params);
  return authenticatedFetch(`/analytics/widgets/${widgetName}${queryString}`, { method: 'GET' });
};

export const getAnomalies = async () => {
  try {
    const response = await authenticatedFetch('/analytics/anomalies');
    // --- UPDATE: the endpoint may answer with the array itself; `response.data` was undefined then
    return Array.isArray(response) ? response : (response?.data ?? []);
    // --- END UPDATE
  } catch (error) {
    console.error('Error fetching anomaly data:', error);
    return [];
  }
};

// --- UPDATE: Added getAiDispatchSummaryStats function ---
/**
 * Fetches summary statistics for AI Dispatch Performance.
 * This is used by the AI & Learning dashboard.
 */
export const getAiDispatchSummaryStats = () => {
    return authenticatedFetch(`/analytics/ai-dispatch-summary`);
};

export const getAiDispatchFeedbackLog = (filters = { page: 1, limit: 10 }) => {
    const params = new URLSearchParams(filters).toString();
    return authenticatedFetch(`/analytics/ai-dispatch-log?${params}`);
};

// --- UPDATE: Added functions for Predictive Health Dashboard ---
/**
 * Fetches the most recent health score for all assets.
 */
export const getLatestAssetHealthScores = () => {
    return authenticatedFetch(`/predictive/latest-health-scores`);
};

export const getAssetHealthScores = () => {
    return authenticatedFetch(`/predictive/health-scores`);
};

export const triggerHealthScoreCalculation = () => {
    return authenticatedFetch(`/predictive/calculate-health-scores`, { method: 'POST' });
};

/**
 * Fetches the historical health scores for a single asset.
 * @param {string} assetId - The ID of the asset.
 */
export const getAssetHealthHistory = (assetId) => {
    return authenticatedFetch(`/predictive/health-history/${assetId}`);
};

export const getABCAnalysis = () => {
    return authenticatedFetch(`/analytics/inventory/abc-analysis`);
};

/**
 * Fetches the dynamic stock level calculations for all inventory items.
 */
export const getDynamicStockLevels = () => {
    return authenticatedFetch(`/analytics/inventory/dynamic-stock-levels`);
};

/**
 * Fetches the performance metrics for all suppliers.
 */
export const getSupplierPerformance = () => {
    return authenticatedFetch(`/analytics/inventory/supplier-performance`);
};

/**
 * Fetches advanced reliability metrics (MTTR, MTBF, FTFR) for all asset types.
 * This is the specific named export used by some widgets.
 */
export const getAdvancedReliabilityMetrics = () => {
    return authenticatedFetch(`/analytics/assets/reliability-metrics`);
};

/**
 * Fetches reliability metrics.
 * Alias/Wrapper for getAdvancedReliabilityMetrics to match widget calls.
 */
// --- UPDATE: forwards the widget's filters (e.g. { timeRange: '90days' }); they were dropped before
export const getReliabilityMetrics = (params) => {
    return authenticatedFetch(`/analytics/assets/reliability-metrics${createQueryString(params)}`);
};
// --- END UPDATE

/**
 * Fetches the list of problem assets.
 * Corresponds to the 'problem-assets' widget.
 */
export const getProblemAssets = (params) => {
    const queryString = createQueryString(params);
    return authenticatedFetch(`/analytics/widgets/problem-assets${queryString}`);
};

/**
 * Fetches the asset downtime data.
 * @returns {Promise<object>} The API response.
 */
// --- UPDATE: optional filters (startDate / endDate / days) — no params keeps the old call exactly
export const getAssetDowntime = (params) => {
    return authenticatedFetch(`/analytics/assets/downtime${createQueryString(params)}`);
};
// --- END UPDATE

// --- UPDATE: getAssetLifecycleCost now accepts an AbortSignal so the
//             LifecycleCostWidget can cancel an in-flight request on unmount
//             or StrictMode's synthetic cleanup cycle.
/**
 * Fetches the lifecycle cost analysis for all asset types.
 * @param {AbortSignal} [signal] - Optional AbortController signal for cancellation.
 */
export const getAssetLifecycleCost = (signal) => {
    return authenticatedFetch(`/analytics/assets/lifecycle-cost`, { method: 'GET', signal });
};
// --- END UPDATE ---

/**
 * Fetches the total cost of ownership analysis for all assets.
 */
export const getAssetTotalCostOfOwnership = (params) => {
    const queryString = createQueryString(params);
    return authenticatedFetch(`/analytics/assets/total-cost-of-ownership${queryString}`);
};

export const getAssetLifecycleDetails = (assetId) => {
    return authenticatedFetch(`/analytics/assets/${assetId}/lifecycle-details`);
};

// 🚀 NEW UPDATE: Added getAssetPerformance
/**
 * Fetches real-time performance analytics for a specific asset (OEE, MTBF, Downtime).
 */
export const getAssetPerformance = async (assetId) => {
  try {
    const endpoint = `/analytics/asset-performance?asset_id=${assetId}`;
    const response = await authenticatedFetch(endpoint);
    return response;
  } catch (error) {
    console.error('[Analytics Service] Error fetching asset performance:', error);
    throw error;
  }
};

/**
 * Triggers the training for the AI dispatch model.
 * --- UPDATE (ML Ops): a tracked control-plane run; resolves with the outcome
 * { jobId, status: 'production'|'staging'|'skipped'|'running', decision, model_version, message }
 * — the shape DispatcherTrainingWidget already reads. onProgress(job) receives live progress.
 */
export const trainDispatchModel = async ({ onProgress, signal } = {}) => {
  return mlOpsService.trainAndWait('DISPATCHER', { onProgress, signal });
};
// --- END UPDATE ---

/**
 * Fetches insights from the active RUL prediction model from the backend.
 * @returns {Promise<object>} A promise that resolves to the model insights.
 */
export const getMlModelInsights = async () => {
    return await authenticatedFetch('/analytics/ml-model-insights');
};

/**
 * Fetches aggregated NLP insights from the backend.
 * @param {object} dateRange - { startDate, endDate } or string ('30d')
 * @returns {Promise<object>} - { topMentionedParts: [], commonPhrases: [] }
 */
export const getNlpInsights = async (dateRange) => {
  // Handle if dateRange is a string (e.g. '30d') or object
  let params = new URLSearchParams();
  
  if (typeof dateRange === 'string') {
      params.append('range', dateRange);
  } else {
      const safeRange = dateRange || getDefaultDateRange();
      if (safeRange.startDate) params.append('startDate', safeRange.startDate.toISOString());
      if (safeRange.endDate) params.append('endDate', safeRange.endDate.toISOString());
  }

  try {
    // Matches the backend route we discussed: /api/v1/analytics/nlp/insights
    const data = await authenticatedFetch(`/analytics/nlp/insights?${params.toString()}`);
    return data;
  } catch (error) {
    console.error('Error fetching NLP insights:', error);
    throw error;
  }
};

/**
 * Fetches analyzed technician notes with AI extractions.
 * @param {object} dateRange - { startDate, endDate } or string ('30d')
 * @returns {Promise<object>} - { notes: [] }
 */
export const getAnalyzedNotes = async (dateRange) => {
  let params = new URLSearchParams();
  
  if (typeof dateRange === 'string') {
      params.append('range', dateRange);
  } else {
      const safeRange = dateRange || getDefaultDateRange();
      if (safeRange.startDate) params.append('startDate', safeRange.startDate.toISOString());
      if (safeRange.endDate) params.append('endDate', safeRange.endDate.toISOString());
  }

  try {
    // Matches the backend route: /api/v1/analytics/nlp/notes
    const data = await authenticatedFetch(`/analytics/nlp/notes?${params.toString()}`);
    return data;
  } catch (error) {
    console.error('Error fetching analyzed notes:', error);
    throw error;
  }
};

/**
 * Fetches dispatch assignment probabilities for recent work orders.
 * This is used by the Dispatcher Training Widget.
 * @returns {Promise<object>} The probabilities data.
 */
export const getDispatchProbabilities = async () => {
    // Assuming the backend endpoint is '/analytics/dispatch-probabilities'
    return authenticatedFetch('/analytics/dispatch-probabilities');
};

// FIX: Added default value for dateRange to prevent "Cannot read properties of undefined (reading 'startDate')"
// --- UPDATE: getPrescriptiveAnalyticsSummary now accepts an AbortSignal so
//             LifecycleCostWidget can cancel a slow summary fetch independently
//             of the (usually faster) lifecycle-cost fetch that runs in parallel.
/**
 * Fetches the prescriptive analytics summary for the given date range.
 * @param {object} dateRange - { startDate, endDate }
 * @param {AbortSignal} [signal] - Optional AbortController signal for cancellation.
 */
export const getPrescriptiveAnalyticsSummary = async (dateRange, signal) => {
  const safeRange = dateRange || getDefaultDateRange();
  
  const params = new URLSearchParams({
    startDate: safeRange.startDate.toISOString(),
    endDate: safeRange.endDate.toISOString(),
  });
  return authenticatedFetch(`/analytics/prescriptive-summary?${params.toString()}`, { signal });
};
// --- END UPDATE ---

// --- UPDATE: getPrescriptiveHistory now accepts an AbortSignal so the
//             initial-load sequence in LifecycleCostWidget can cancel an
//             in-flight history request when the component unmounts (StrictMode
//             double-invoke safe).
/**
 * Fetches the full prescriptive action history (ledger).
 * @param {object} [dateRange] - Optional date range (currently unused server-side, reserved for future filtering).
 * @param {AbortSignal} [signal] - Optional AbortController signal for cancellation.
 */
export const getPrescriptiveHistory = async (dateRange, signal) => {
  return authenticatedFetch('/analytics/prescriptive-history', { signal });
};
// --- END UPDATE ---

// --- 🚀 FK GUARD UPDATE: Validate payload shape client-side before hitting the API.
// This prevents malformed payloads from reaching the backend (which would otherwise
// trigger a FK constraint violation or 500 error). Surfaces a clean UI-level error
// so the calling component can display a meaningful message. ---
export const applyPrescriptiveAction = async (payload) => {
  // Normalize: support both legacy `assetId` and snake_case `asset_id` shapes
  const assetId = payload?.assetId || payload?.asset_id;

  if (!assetId || typeof assetId !== 'string' || assetId.trim() === '') {
    const err = new Error('Cannot apply prescriptive action: assetId is missing or invalid. The recommendation may be stale — please refresh and try again.');
    err.code = 'INVALID_ASSET_ID';
    console.error('[Analytics Service] applyPrescriptiveAction rejected:', err.message, 'Payload keys:', Object.keys(payload || {}));
    throw err;
  }

  return authenticatedFetch('/analytics/apply-prescription', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
};
// --- END FK GUARD UPDATE ---


// =========================================================================
// 🚀 ENTERPRISE UPDATE: Unified Sensor Telemetry & WebSocket Integration 
// Fetches readings from the database OR subscribes directly to live sockets.
// =========================================================================

/**
 * Fetches historical pressure/sensor readings from the database.
 */
export const getPressureReadingsHistory = async (params) => {
  const queryString = createQueryString(params);
  return authenticatedFetch(`/readings${queryString}`);
};

/**
 * Fetches historical water level readings from the database.
 */
export const getWaterLevelHistory = async (deviceId, params) => {
  const queryString = createQueryString(params);
  return authenticatedFetch(`/water-level/${deviceId}${queryString}`);
};

/**
 * Subscribes a callback to live sensor readings via the global WebSocket stream.
 * Automatically catches both pressure and water level telemetry.
 * 
 * @param {function} callback - Function to execute when new live data arrives.
 * @returns {function} Unsubscribe function to clean up the listener on unmount.
 */
export const subscribeToLiveSensorReadings = (callback) => {
  webSocketService.subscribe('new_pressure_reading', callback);
  webSocketService.subscribe('new_water_level_reading', callback);
  webSocketService.subscribe('psi_update', callback);
  webSocketService.subscribe('telemetry', callback);

  return () => {
    webSocketService.unsubscribe('new_pressure_reading', callback);
    webSocketService.unsubscribe('new_water_level_reading', callback);
    webSocketService.unsubscribe('psi_update', callback);
    webSocketService.unsubscribe('telemetry', callback);
  };
};
// =========================================================================


// =========================================================================
// --- UPDATE (Analytics Hub): AI briefing, AI activity feed, live analytics events ---
// =========================================================================

// --- UPDATE (round 4): server-generated AI insight cards (alert spikes, RUL-critical assets, skill
// gaps, models needing attention) — each with source, evidence and a link. Returns an array.
export const getAiInsights = async () => {
  const res = await authenticatedFetch('/analytics/ai-insights');
  const list = Array.isArray(res) ? res : (res?.data ?? res?.insights ?? []);
  return Array.isArray(list) ? list : [];
};
// --- END UPDATE ---

/** AI briefing for a date range: KPIs vs the previous period, insights, model status, narrative. */
export const getAiBriefing = ({ startDate, endDate, focus, narrative = true, signal } = {}) =>
  mlOpsService.getBriefing({ startDate, endDate, focus, narrative, signal });

/** Newest AI activity entries (model changes, predictions, critical risks, analysis runs). */
export const getAiActivity = (opts = {}) => mlOpsService.getActivity(opts);

/**
 * Websocket events that change what the Analytics Hub shows. Each arrives as { type, payload }.
 * Exported so dashboards can subscribe to the same list.
 */
export const ANALYTICS_LIVE_EVENTS = Object.freeze([
  'ai_activity', 'ml_events', 'ai_model_promoted', 'ai_feedback_recorded',
  'critical_anomaly', 'new_sensor_alert', 'asset_prediction_update', 'predictive_risk_update',
  'predictive_analysis_complete', 'rul_batch_complete', 'ai_flood_prediction', 'nlp_feedback_processed',
  // --- UPDATE (round 3): predictive alerts resolved / rated / deleted, health scores recalculated
  'predictive_alerts_updated', 'asset_health_scores_updated',
  // --- END UPDATE
]);

/**
 * Subscribe to every analytics-relevant live event. callback(type, payload, rawMessage).
 * @returns {function} unsubscribe
 */
export const subscribeToAnalyticsLive = (callback, events = ANALYTICS_LIVE_EVENTS) => {
  const handlers = events.map((type) => {
    const h = (msg) => {
      try { callback(type, msg && typeof msg === 'object' && 'payload' in msg ? msg.payload : msg, msg); } catch (e) { console.error('[Analytics Service] live handler failed:', e); }
    };
    webSocketService.subscribe(type, h);
    return [type, h];
  });
  return () => handlers.forEach(([type, h]) => webSocketService.unsubscribe(type, h));
};
// --- END UPDATE ---

export const fetchWorkOrderStats = () => {
    const endpoint = `/analytics/work-order-stats`;
    return authenticatedFetch(endpoint);
};

export const fetchTechnicianPerformance = () => {
    const endpoint = `/analytics/technician-performance`;
    return authenticatedFetch(endpoint);
};

export const getWorkOrderByPriority = (params) => {
  return getWidgetData('work-order-priority-distribution', params);
};

export const getIssueHeatmapData = (params) => {
  return getWidgetData('issue-hotspots', params);
};

export const getWorkOrderFlowData = (params) => {
  return getWidgetData('work-order-flow', params);
};

export const getDataStory = (params) => {
  return getWidgetData('data-story', params);
};

export const getRulModelInsights = (params) => {
  return getWidgetData('rul-model-insights', params);
};

export const predictAssetFailure = async (assetId) => {
    if (!assetId) {
      throw new Error('Asset ID is required for prediction.');
    }
    return authenticatedFetch(`/predictive-failure/predict/${assetId}`);
};

/**
 * Fetches the most recent RUL prediction/insight for a specific asset.
 */
export const getLatestAssetPrediction = async (assetId) => {
  try {
    // FIX: URL matched to backend predictiveFailureRoutes.js
    const response = await authenticatedFetch(`/predictive-failure/health/${assetId}`);
    return response; 
  } catch (error) {
    console.warn(`[Analytics Service] No active prediction found for asset ${assetId}.`);
    return null; 
  }
};

/**
 * Persists an AI-generated RUL prediction to the database.
 */
export const saveAssetPrediction = async (payload) => {
  return authenticatedFetch('/predictive-failure/save-prediction', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
};

export const getAssetCostBreakdown = (assetId) => {
  return authenticatedFetch(`/analytics/assets/${assetId}/cost-breakdown`);
};

// --- UPDATE (FlowSense widgets) ---
/**
 * Creates a work order (ProblemAssetsWidget "Schedule maintenance" called this, but it did not exist,
 * so scheduling always failed). POST /workorders — the same base the watchdog routes use.
 */
export const createWorkOrder = (payload) => authenticatedFetch('/workorders', {
  method: 'POST',
  body: JSON.stringify(payload || {}),
});

/**
 * Websocket listeners receive the whole envelope { type: <event>, payload }. Widgets that read
 * payload.type (scan_complete, circuit_open, sla_watchdog_update …) need the inner object.
 */
export const liveEventBody = (msg) => (
  msg && typeof msg === 'object' && msg.payload && typeof msg.payload === 'object' ? msg.payload : (msg || {})
);

/** Normalises the many list shapes the analytics endpoints return: [], {data:[]}, {data:{data:[]}}. */
export const asList = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.rows)) return res.rows;
  if (Array.isArray(res?.items)) return res.items;
  return [];
};
// --- END UPDATE ---

// Default export with all methods
const analyticsService = {
  getKpis,
  getWidgetData,
  getAnomalies,
  getAiDispatchSummaryStats,
  getAiDispatchFeedbackLog,
  getLatestAssetHealthScores,
  getAssetHealthScores,
  triggerHealthScoreCalculation,
  getAssetHealthHistory,
  getABCAnalysis,
  getDynamicStockLevels,
  getSupplierPerformance,
  getAdvancedReliabilityMetrics,
  getReliabilityMetrics,
  getProblemAssets,
  getAssetDowntime,
  getAssetLifecycleCost,
  getAssetTotalCostOfOwnership,
  getAssetLifecycleDetails,
  getAssetPerformance, 
  trainDispatchModel,
  getMlModelInsights,
  getNlpInsights,
  getAnalyzedNotes,
  getDispatchProbabilities,
  getPrescriptiveAnalyticsSummary,
  getPrescriptiveHistory,
  applyPrescriptiveAction,
  
  // 🚀 NEW: Unified Telemetry
  getPressureReadingsHistory,
  getWaterLevelHistory,
  subscribeToLiveSensorReadings,

  fetchWorkOrderStats,
  fetchTechnicianPerformance,
  getWorkOrderByPriority,
  getIssueHeatmapData,
  getWorkOrderFlowData,
  getDataStory,
  getRulModelInsights,
  getAiInsights, // --- UPDATE (round 4)
  predictAssetFailure,
  getLatestAssetPrediction,
  saveAssetPrediction,
  getAssetCostBreakdown,
  createWorkOrder, liveEventBody, asList, // --- UPDATE (FlowSense widgets)
  // --- UPDATE (Analytics Hub) ---
  getAiBriefing,
  getAiActivity,
  subscribeToAnalyticsLive,
  ANALYTICS_LIVE_EVENTS,
  // --- END UPDATE ---
};

export default analyticsService;